import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Sparkles, ArrowRight, CheckCircle2, Clock, FileCheck, Layers, ChevronLeft } from 'lucide-react';

import { Breadcrumb } from '../../components/common/Breadcrumb';

export const ServiceDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: service, isLoading, error } = useQuery({
    queryKey: ['public-service-detail', slug],
    queryFn: () => api.get(`/public/services/${slug}`).then((res) => res.data.data),
    enabled: !!slug,
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-xs font-mono">Loading service details...</div>;
  }

  if (error || !service) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-serif font-bold text-slate-900">Service Not Found</h2>
        <Link to="/services" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to all services
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-12 py-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'Services', href: '/services' },
            { label: service.title },
          ]}
        />
      </div>

      {/* Header */}
      <div className="space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-50 border border-brand-200 text-brand-800 text-xs font-mono font-bold uppercase">
          {service.category}
        </div>
        <h1 className="text-3xl sm:text-5xl font-serif font-bold text-editorial-dark">
          {service.title}
        </h1>
        <p className="text-base sm:text-lg text-slate-600 font-sans leading-relaxed">
          {service.summary}
        </p>
        <div className="flex items-center gap-4 text-xs text-slate-500 font-mono pt-2">
          <span>Estimated Timeline: <strong>{service.timeline}</strong></span>
        </div>
      </div>

      {/* Full Description */}
      <div className="p-8 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6">
        <h2 className="font-serif text-2xl font-bold text-slate-900">Overview &amp; Scope</h2>
        <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-sans">
          {service.description}
        </p>

        {/* Included Items */}
        {service.includedItems && (
          <div className="space-y-3 pt-4 border-t border-slate-100">
            <h3 className="font-serif text-lg font-bold text-slate-900">What is Included</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.includedItems.map((item: string, idx: number) => (
                <div key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                  <CheckCircle2 className="w-4 h-4 text-brand-600 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Process Steps */}
        {service.processSteps && (
          <div className="space-y-4 pt-6 border-t border-slate-100">
            <h3 className="font-serif text-lg font-bold text-slate-900">Service Process</h3>
            <div className="space-y-3">
              {service.processSteps.map((step: any, idx: number) => (
                <div key={idx} className="p-4 rounded-lg bg-editorial-parchment border border-editorial-border space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-brand-200 text-brand-900 text-xs font-bold font-mono flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <h4 className="font-serif font-bold text-sm text-slate-900">{step.title}</h4>
                  </div>
                  <p className="text-xs text-slate-600 pl-7">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Deliverables */}
        {service.deliverables && (
          <div className="space-y-3 pt-6 border-t border-slate-100">
            <h3 className="font-serif text-lg font-bold text-slate-900">Final Deliverables</h3>
            <ul className="space-y-2">
              {service.deliverables.map((del: string, idx: number) => (
                <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-slate-700 font-medium">
                  <FileCheck className="w-4 h-4 text-brand-600 shrink-0" />
                  <span>{del}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* CTA Box */}
      <div className="p-8 rounded-2xl bg-editorial-dark text-white flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="space-y-1 text-center sm:text-left">
          <h3 className="font-serif text-xl font-bold">Ready to Start This Service?</h3>
          <p className="text-xs text-slate-400">Submit an initial enquiry with your manuscript details.</p>
        </div>
        <Link
          to="/submit-enquiry"
          className="px-6 py-3 rounded-lg bg-brand-500 hover:bg-brand-600 text-slate-950 font-bold text-xs shrink-0 transition-colors"
        >
          Submit Enquiry
        </Link>
      </div>

    </div>
  );
};
